'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  initDB,
  closeDatabase,
  deleteDatabase,
  getLocalDbFile,
  getLatestManifest,
  requestForceDownload
} from '../lib/sqlite-engine'
import type { OfflineDbFileInfo } from '../lib/sqlite-engine'

function getErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message || error.name
  }
  if (typeof error === 'string') {
    return error
  }
  try {
    return JSON.stringify(error)
  } catch {
    return 'Unknown error'
  }
}

function detectOfflineSupport() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }
  if (!window.isSecureContext) {
    return false
  }
  // OPFS VFS in the bundled sqlite build requires BOTH SharedArrayBuffer/Atomics
  // (i.e. cross‑origin isolation via COOP/COEP) AND the OPFS APIs.
  // If SharedArrayBuffer is missing, sqlite3.js will refuse to install the
  // "opfs" VFS, so opening `?vfs=opfs` will error with "no such vfs: opfs".
  const hasSharedArrayBuffer =
    typeof window.SharedArrayBuffer !== 'undefined' ||
    typeof globalThis.SharedArrayBuffer !== 'undefined'
  const hasOpfs =
    'storage' in navigator &&
    typeof navigator.storage?.getDirectory === 'function'

  return hasSharedArrayBuffer && hasOpfs
}

const UNSUPPORTED_MESSAGE =
  'Offline downloads are not available in this browser context.'

export function useOfflineManager() {
  const [isReady, setIsReady] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [statusText, setStatusText] = useState('')
  const [offlineError, setOfflineError] = useState<string | null>(null)
  const [hasOfflineData, setHasOfflineData] = useState(false)
  const [localFile, setLocalFile] = useState<OfflineDbFileInfo | null>(null)
  const [currentVersion, setCurrentVersion] = useState<string | null>(null)
  const [latestVersion, setLatestVersion] = useState<string | null>(null)
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false)
  const [manifestTiers, setManifestTiers] = useState<{
    premium?: {
      file: string
      entryCount: number
      sizeBytes: number
      checksum: string
    }
  }>({})
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(true) // Default to true
  const abortControllerRef = useRef<AbortController | null>(null)
  const [isOfflineSupported, setIsOfflineSupported] = useState(false) // Start with false, will be detected after mount

  // We persist the user's preference in localStorage
  const [isOfflineEnabled, setIsOfflineEnabled] = useState(false) // Default to false, will be set in useEffect

  // Initialize from localStorage on client side only and detect offline support
  useEffect(() => {
    if (typeof window === 'undefined') return

    // Detect offline support after mount (headers need to be applied)
    const supported = detectOfflineSupport()
    setIsOfflineSupported(supported)
    if (supported) {
      setStatusText((current) =>
        current === UNSUPPORTED_MESSAGE ? '' : current
      )
    }

    // Load initial values from localStorage
    const storedVersion = localStorage.getItem('dict_version')
    if (storedVersion) {
      setCurrentVersion(storedVersion)
    }

    const storedAutoUpdate = localStorage.getItem('offline-auto-update')
    if (storedAutoUpdate !== null) {
      setAutoUpdateEnabled(storedAutoUpdate !== 'false')
    }

    const storedOfflineMode = localStorage.getItem('offline-mode')
    if (storedOfflineMode !== null) {
      setIsOfflineEnabled(storedOfflineMode === 'true')
    }

    // Mark that we've loaded persisted preferences so queries can safely use them
    setIsHydrated(true)
  }, [])

  const refreshMetadata = useCallback(async () => {
    try {
      const manifestResult = await getLatestManifest()
      setLatestVersion(manifestResult.manifest?.version ?? null)
      setManifestTiers(manifestResult.manifest?.tiers ?? {})

      if (!isOfflineSupported) {
        setLocalFile(null)
        setHasOfflineData(false)
        setCurrentVersion(null)
        setIsUpdateAvailable(false)
        return
      }

      const file = await getLocalDbFile()
      setLocalFile(file)
      setHasOfflineData(Boolean(file))
      setCurrentVersion(file?.version ?? null)
      setIsUpdateAvailable(
        Boolean(
          manifestResult.manifest &&
            file?.version &&
            (file.version !== manifestResult.manifest.version ||
              file.tier !== 'premium')
        )
      )
    } catch (err) {
      console.error('Failed to refresh offline metadata', err)
    }
  }, [isOfflineSupported])

  useEffect(() => {
    refreshMetadata()
  }, [refreshMetadata])

  useEffect(() => {
    if (isOfflineSupported) {
      setStatusText((current) =>
        current === UNSUPPORTED_MESSAGE ? '' : current
      )
      return
    }
    if (isOfflineEnabled) {
      setIsOfflineEnabled(false)
      localStorage.setItem('offline-mode', 'false')
    }
    setStatusText(UNSUPPORTED_MESSAGE)
  }, [isOfflineSupported, isOfflineEnabled])

  const toggleOffline = useCallback(() => {
    if (!isOfflineSupported) {
      setStatusText(UNSUPPORTED_MESSAGE)
      return
    }
    const newState = !isOfflineEnabled
    setIsOfflineEnabled(newState)
    localStorage.setItem('offline-mode', String(newState))
    if (!newState) {
      setIsReady(false)
      setStatusText('Offline mode disabled')
      setOfflineError(null)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
        setProgress(null)
      }
    }
  }, [isOfflineEnabled, isOfflineSupported])

  const redownloadDictionary = useCallback(async () => {
    if (!isOfflineSupported) {
      setStatusText(UNSUPPORTED_MESSAGE)
      return
    }
    // Close existing SQLite connection before redownloading
    // This ensures the OPFS file handle is released
    await closeDatabase()

    requestForceDownload()

    if (!isOfflineEnabled) {
      setIsOfflineEnabled(true)
      localStorage.setItem('offline-mode', 'true')
    } else {
      setIsReady(false)
      setOfflineError(null)
      void refreshMetadata()
    }
  }, [isOfflineEnabled, isOfflineSupported, refreshMetadata])

  const deleteDictionary = useCallback(async () => {
    if (
      !confirm(
        'Are you sure you want to delete the offline dictionary? You will need to download it again to use offline search.'
      )
    ) {
      return
    }

    try {
      setStatusText('Deleting...')

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }

      setIsOfflineEnabled(false)
      localStorage.setItem('offline-mode', 'false')
      setIsReady(false)
      setOfflineError(null)

      await deleteDatabase()

      setHasOfflineData(false)
      setStatusText('Offline data removed')
      setProgress(null)
      await refreshMetadata()
    } catch (e) {
      console.error('Failed to delete dictionary', e)
      setError(e as Error)
    }
  }, [refreshMetadata])

  const updateAutoUpdatePreference = useCallback(
    (enabled: boolean) => {
      setAutoUpdateEnabled(enabled)
      localStorage.setItem('offline-auto-update', String(enabled))
      if (enabled) {
        void refreshMetadata()
      }
    },
    [refreshMetadata]
  )

  useEffect(() => {
    if (
      !isOfflineSupported ||
      !autoUpdateEnabled ||
      !isOfflineEnabled ||
      !isUpdateAvailable ||
      progress !== null
    ) {
      return
    }
    redownloadDictionary()
  }, [
    autoUpdateEnabled,
    isOfflineEnabled,
    isOfflineSupported,
    isUpdateAvailable,
    progress,
    redownloadDictionary
  ])

  useEffect(() => {
    if (!isOfflineSupported || !isOfflineEnabled) {
      return
    }

    // Start initialization
    const startEngine = async () => {
      let controller: AbortController | null = null
      try {
        // If already ready, skip
        if (isReady) return

        // Create new abort controller
        controller = new AbortController()
        abortControllerRef.current = controller

        setProgress(0)
        setError(null)
        setStatusText('Initializing...')

        await initDB((pct) => {
          if (controller && !controller.signal.aborted) {
            setProgress(pct)
            setStatusText(
              pct < 100 ? 'Downloading dictionary...' : 'Booting engine...'
            )
          }
        })

        if (controller && !controller.signal.aborted) {
          setProgress(100)
          setStatusText('Booting engine...')
          await new Promise((resolve) => setTimeout(resolve, 500))
          if (controller && !controller.signal.aborted) {
            setIsReady(true)
            setHasOfflineData(true)
            setStatusText('Ready')
            setOfflineError(null)
            await refreshMetadata()
          }
        }
      } catch (e) {
        // Ignore abort errors
        if (e instanceof DOMException && e.name === 'AbortError') {
          console.log('Download aborted')
          return
        }

        console.error('Offline Init Failed:', e)
        setError(e as Error)
        setIsOfflineEnabled(false) // Fallback to online
        localStorage.setItem('offline-mode', 'false')
        setStatusText(`Offline initialization failed: ${getErrorMessage(e)}`)
        setOfflineError(getErrorMessage(e))
      } finally {
        if (controller && abortControllerRef.current === controller) {
          setProgress(null)
          abortControllerRef.current = null
        }
      }
    }

    startEngine()

    return () => {
      // Cleanup on unmount or dependency change if needed
      // But we generally want the download to persist if the component re-renders
      // unless the user explicitly turns it off.
    }
  }, [isOfflineEnabled, isOfflineSupported, isReady, refreshMetadata])

  return {
    isOfflineEnabled,
    isReady,
    isHydrated,
    hasOfflineData,
    progress,
    statusText,
    error,
    toggleOffline,
    deleteDictionary,
    redownloadDictionary,
    localFile,
    currentVersion,
    latestVersion,
    isUpdateAvailable,
    autoUpdateEnabled,
    setAutoUpdateEnabled: updateAutoUpdatePreference,
    isOfflineSupported,
    offlineSupportMessage: isOfflineSupported ? null : UNSUPPORTED_MESSAGE,
    offlineError,
    manifestTiers,
    currentTier: localFile?.tier ?? null,
    // Helper to check if queries should be enabled (waits for offline mode if needed)
    isQueryEnabled: (condition: boolean) =>
      condition && isHydrated && (!isOfflineEnabled || isReady)
  }
}
